// ✅ COMPLETE UPDATED: src/components/laboratory/forms/CombinedLabUserForm.jsx
// Fixed with correct CREATE_USER mutation format and proper password handling

import React, { useState, useEffect } from 'react';
import { useMutation, gql } from '@apollo/client';
import { CREATE_LABORATORY, UPDATE_LABORATORY } from '../../../api/laboratory-api';
import ModalComponent from '../../common/ModalLabo';
import { Info, Building2, User, MapPin, FileText, Lock, Eye, EyeOff } from 'lucide-react';
import { toast } from 'react-toastify';

// ✅ FIXED: Use correct CREATE_USER mutation format
const CREATE_USER = gql`
  mutation CreateUser($email: String!, $password: String!, $userType: String!, $firstName: String, $lastName: String, $tel: String, $isActive: Boolean) {
    createUser(email: $email, password: $password, userType: $userType, firstName: $firstName, lastName: $lastName, tel: $tel, isActive: $isActive) {
      id
      email
      firstName
      lastName
      tel
      userType
      isActive
      isPhoneVerified
      createdAt
      updatedAt
    }
  }
`;

const CombinedLabUserForm = ({
  isOpen,
  onClose,
  onSuccess,
  laboratory = null,
  mode = 'create' // 'create' or 'edit'
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [formData, setFormData] = useState({
    laboratory: {
      nom: '',
      raisonSociale: '',
      adresse: '',
      registre: '',
      ice: '',
      job: '',
    },
    user: {
      email: '',
      password: '',
      confirmPassword: '',
      firstName: '',
      lastName: '',
      tel: '',
    }
  });

  const [formErrors, setFormErrors] = useState({
    laboratory: {},
    user: {}
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setCurrentStep(1);
      setFormErrors({ laboratory: {}, user: {} });
      if (mode === 'create') {
        setFormData({
          laboratory: {
            nom: '',
            raisonSociale: '',
            adresse: '',
            registre: '',
            ice: '',
            job: '',
          },
          user: {
            email: '',
            password: '',
            confirmPassword: '',
            firstName: '',
            lastName: '',
            tel: '',
          }
        });
      }
    }
  }, [isOpen, mode]);

  // Initialize form data for edit mode
  useEffect(() => {
    if (laboratory && mode === 'edit') {
      setFormData(prev => ({
        ...prev,
        laboratory: {
          nom: laboratory.nom || '',
          raisonSociale: laboratory.raisonSociale || '',
          adresse: laboratory.adresse || '',
          registre: laboratory.registre || '',
          ice: laboratory.ice || '',
          job: laboratory.job || '',
        }
      }));
    }
  }, [laboratory, mode]);

  // ✅ FIXED: Updated CREATE_USER mutation with proper response handling
  const [createUser] = useMutation(CREATE_USER, {
    onCompleted: (data) => {
      if (data.createUser) {
        const userId = data.createUser.id;
        toast.success('Utilisateur créé avec succès');

        // Create laboratory with the new user
        createLaboratory({
          variables: {
            createLaboratoryInput: {
              nom: formData.laboratory.nom,
              raisonSociale: formData.laboratory.raisonSociale || null,
              adresse: formData.laboratory.adresse || null,
              registre: formData.laboratory.registre || null,
              ice: formData.laboratory.ice || null,
              job: formData.laboratory.job || null,
              userId: userId
            }
          }
        });
      } else {
        toast.error('Erreur lors de la création de l\'utilisateur');
        setIsSubmitting(false);
      }
    },
    onError: (error) => {
      console.error('Create user error:', error);
      toast.error(`Erreur lors de la création de l'utilisateur: ${error.message}`);
      setIsSubmitting(false);
    }
  });

  const [createLaboratory] = useMutation(CREATE_LABORATORY, {
    onCompleted: (data) => {
      if (data.createLaboratory) {
        toast.success('Laboratoire créé avec succès');
        setIsSubmitting(false);
        onSuccess();
      } else {
        toast.error('Erreur lors de la création du laboratoire');
        setIsSubmitting(false);
      }
    },
    onError: (error) => {
      console.error('Create laboratory error:', error);
      toast.error(`Erreur lors de la création du laboratoire: ${error.message}`);
      setIsSubmitting(false);
    }
  });

  const [updateLaboratory] = useMutation(UPDATE_LABORATORY, {
    onCompleted: (data) => {
      if (data.updateLaboratory) {
        toast.success('Laboratoire mis à jour avec succès');
        setIsSubmitting(false);
        onSuccess();
      } else {
        toast.error('Erreur lors de la mise à jour du laboratoire');
        setIsSubmitting(false);
      }
    },
    onError: (error) => {
      console.error('Update laboratory error:', error);
      toast.error(`Erreur lors de la mise à jour du laboratoire: ${error.message}`);
      setIsSubmitting(false);
    }
  });

  // Form input handler
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name.startsWith('laboratory.')) {
      const fieldName = name.replace('laboratory.', '');
      setFormData(prev => ({
        ...prev,
        laboratory: {
          ...prev.laboratory,
          [fieldName]: value
        }
      }));

      // Clear error if field was in error
      if (value && formErrors.laboratory[fieldName]) {
        setFormErrors(prev => ({
          ...prev,
          laboratory: {
            ...prev.laboratory,
            [fieldName]: undefined
          }
        }));
      }
    } else if (name.startsWith('user.')) {
      const fieldName = name.replace('user.', '');
      setFormData(prev => ({
        ...prev,
        user: {
          ...prev.user,
          [fieldName]: value
        }
      }));

      // Clear error if field was in error
      if (value && formErrors.user[fieldName]) {
        setFormErrors(prev => ({
          ...prev,
          user: {
            ...prev.user,
            [fieldName]: undefined
          }
        }));
      }
    }
  };

  // Validation functions
  const validateLaboratoryStep = () => {
    const errors = {};

    // Check if the laboratory name exists and is not empty
    if (!formData.laboratory.nom || !formData.laboratory.nom.trim()) {
      errors.nom = 'Le nom du laboratoire est requis';
    }

    // ICE validation - only validate if ICE is provided
    if (formData.laboratory.ice && formData.laboratory.ice.trim()) {
      if (!/^\d{15}$/.test(formData.laboratory.ice.trim())) {
        errors.ice = 'L\'ICE doit contenir exactement 15 chiffres';
      }
    }

    // Update form errors
    setFormErrors(prev => ({
      ...prev,
      laboratory: errors
    }));

    // Return true if no errors
    return Object.keys(errors).length === 0;
  };

  const validateUserStep = () => {
    const errors = {};

    if (!formData.user.email.trim()) {
      errors.email = 'L\'email est requis';
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.user.email)) {
      errors.email = 'Format d\'email invalide';
    }

    if (!formData.user.password) {
      errors.password = 'Le mot de passe est requis';
    } else if (formData.user.password.length < 6) {
      errors.password = 'Le mot de passe doit contenir au moins 6 caractères';
    }

    if (formData.user.password !== formData.user.confirmPassword) {
      errors.confirmPassword = 'Les mots de passe ne correspondent pas';
    }

    if (formData.user.tel && !/^(\+212|0)[5-7][0-9]{8}$/.test(formData.user.tel)) {
      errors.tel = 'Format de téléphone invalide (ex: +212 5XX XXX XXX)';
    }

    setFormErrors(prev => ({ ...prev, user: errors }));
    return Object.keys(errors).length === 0;
  };

  // Step navigation - only for create mode
  const handleNextStep = () => {
    console.log('handleNextStep called, current step:', currentStep);
    console.log('mode:', mode);
    console.log('form data laboratory:', formData.laboratory);

    if (mode === 'edit') return;

    if (currentStep === 1) {
      const isValid = validateLaboratoryStep();
      console.log('validation result:', isValid);
      console.log('form errors after validation:', formErrors.laboratory);

      if (isValid) {
        console.log('Moving to step 2');
        setCurrentStep(2);
      } else {
        console.log('Validation failed, staying on step 1');
      }
    }
  };

  const handlePrevStep = () => {
    if (mode === 'edit') return;
    if (currentStep === 2) {
      setCurrentStep(1);
    }
  };

  // ✅ FIXED: Form submission with correct CREATE_USER variables
  const handleSubmit = async () => {
    if (mode === 'edit') {
      // Edit mode - update laboratory only
      if (!validateLaboratoryStep()) return;

      setIsSubmitting(true);

      try {
        await updateLaboratory({
          variables: {
            updateLaboratoryInput: {
              id: laboratory.id,
              nom: formData.laboratory.nom,
              raisonSociale: formData.laboratory.raisonSociale || null,
              adresse: formData.laboratory.adresse || null,
              registre: formData.laboratory.registre || null,
              ice: formData.laboratory.ice || null,
              job: formData.laboratory.job || null,
            }
          }
        });
      } catch (error) {
        console.error('Error updating laboratory:', error);
        setIsSubmitting(false);
      }
    } else {
      // Create mode - validate both steps
      if (!validateLaboratoryStep() || !validateUserStep()) return;

      setIsSubmitting(true);

      try {
        // ✅ FIXED: Use correct CREATE_USER variables (password will be hashed by backend)
        await createUser({
          variables: {
            email: formData.user.email,
            password: formData.user.password, // Backend will hash this securely
            userType: "LABORATORY",
            firstName: formData.user.firstName || "",
            lastName: formData.user.lastName || "",
            tel: formData.user.tel || "",
            isActive: false
          }
        });
      } catch (error) {
        console.error('Error creating laboratory with user:', error);
        setIsSubmitting(false);
      }
    }
  };

  // Step indicator component
  const StepIndicator = () => {
    if (mode === 'edit') return null;

    return (
      <div className="flex items-center justify-center mb-6">
        <div className="flex items-center">
          <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${currentStep >= 1
              ? 'bg-green-600 border-green-600 text-white'
              : 'border-gray-300 text-gray-400'
            }`}>
            <Building2 size={16} />
          </div>
          <div className={`h-1 w-16 ${currentStep >= 2 ? 'bg-green-600' : 'bg-gray-300'}`}></div>
          <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${currentStep >= 2
              ? 'bg-green-600 border-green-600 text-white'
              : 'border-gray-300 text-gray-400'
            }`}>
            <User size={16} />
          </div>
        </div>
        <div className="ml-4 text-sm text-gray-600">
          Étape {currentStep} sur 2
        </div>
      </div>
    );
  };

  // Password visibility toggle
  const togglePasswordVisibility = (field) => {
    if (field === 'password') {
      setShowPassword(!showPassword);
    } else if (field === 'confirmPassword') {
      setShowConfirmPassword(!showConfirmPassword);
    }
  };

  // Modal configuration
  const getModalTitle = () => {
    if (mode === 'edit') {
      return `Modifier le laboratoire - ${laboratory?.nom}`;
    }
    return "Créer un nouveau laboratoire";
  };

  const getModalButtons = () => {
    if (mode === 'edit') {
      // Edit mode - no step navigation, just standard submit/cancel
      return {
        onSubmit: handleSubmit,
        isSubmitting: isSubmitting,
        showStepButtons: false
      };
    }

    // Create mode - use step navigation
    return {
      showStepButtons: true,
      currentStep: currentStep,
      totalSteps: 2,
      onNext: handleNextStep,
      onPrev: handlePrevStep,
      onSubmit: handleSubmit,
      isSubmitting: isSubmitting
    };
  };

  return (
    <ModalComponent
      isOpen={isOpen}
      onClose={onClose}
      title={getModalTitle()}
      size="xl"
      {...getModalButtons()}
    >
      <div className="space-y-6">
        <StepIndicator />

        {/* Step 1: Laboratory Information */}
        {currentStep === 1 && (
          <div className="space-y-6">
            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
              <div className="flex items-center mb-4">
                <Building2 className="w-6 h-6 text-green-600 mr-3" />
                <h3 className="text-lg font-semibold text-gray-900">Informations du laboratoire</h3>
              </div>

              <div className="space-y-4">
                {/* Laboratory Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom du laboratoire <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="laboratory.nom"
                    value={formData.laboratory.nom}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors ${formErrors.laboratory.nom ? 'border-red-500 bg-red-50' : 'border-gray-300'
                      }`}
                    placeholder="Entrez le nom du laboratoire"
                    disabled={mode === 'edit'} // Disable name editing in edit mode
                  />
                  {formErrors.laboratory.nom && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.laboratory.nom}</p>
                  )}
                  {mode === 'edit' && (
                    <p className="mt-1 text-xs text-gray-500">Le nom du laboratoire ne peut pas être modifié</p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Raison Sociale */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Raison sociale
                    </label>
                    <input
                      type="text"
                      name="laboratory.raisonSociale"
                      value={formData.laboratory.raisonSociale}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors"
                      placeholder="Raison sociale"
                    />
                  </div>

                  {/* ICE */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      ICE
                    </label>
                    <input
                      type="text"
                      name="laboratory.ice"
                      value={formData.laboratory.ice}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors ${formErrors.laboratory.ice ? 'border-red-500 bg-red-50' : 'border-gray-300'
                        }`}
                      placeholder="ICE (15 chiffres)"
                      maxLength="15"
                    />
                    {formErrors.laboratory.ice && (
                      <p className="mt-1 text-sm text-red-600">{formErrors.laboratory.ice}</p>
                    )}
                  </div>
                </div>

                {/* Address */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <MapPin className="inline w-4 h-4 mr-1" />
                    Adresse
                  </label>
                  <textarea
                    name="laboratory.adresse"
                    value={formData.laboratory.adresse}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors"
                    placeholder="Adresse complète du laboratoire"
                    rows="3"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Registre */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <FileText className="inline w-4 h-4 mr-1" />
                      Registre de commerce
                    </label>
                    <input
                      type="text"
                      name="laboratory.registre"
                      value={formData.laboratory.registre}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors"
                      placeholder="Numéro de registre"
                    />
                  </div>

                  {/* Job */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Spécialité / Fonction
                    </label>
                    <input
                      type="text"
                      name="laboratory.job"
                      value={formData.laboratory.job}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors"
                      placeholder="Domaine de spécialité"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start">
                <Info className="h-5 w-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">Information :</p>
                  <p>
                    {mode === 'edit'
                      ? 'Vous pouvez modifier toutes les informations sauf le nom du laboratoire.'
                      : 'Seul le nom du laboratoire est obligatoire. Toutes les autres informations peuvent être ajoutées maintenant ou ultérieurement.'
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: User Information (only in create mode) */}
        {mode === 'create' && currentStep === 2 && (
          <div className="space-y-6">
            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
              <div className="flex items-center mb-4">
                <User className="w-6 h-6 text-blue-600 mr-3" />
                <h3 className="text-lg font-semibold text-gray-900">Compte utilisateur du propriétaire</h3>
              </div>

              <div className="space-y-4">
                {/* Name Fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Prénom
                    </label>
                    <input
                      type="text"
                      name="user.firstName"
                      value={formData.user.firstName}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors"
                      placeholder="Prénom du propriétaire"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nom
                    </label>
                    <input
                      type="text"
                      name="user.lastName"
                      value={formData.user.lastName}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors"
                      placeholder="Nom du propriétaire"
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="user.email"
                    value={formData.user.email}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors ${formErrors.user.email ? 'border-red-500 bg-red-50' : 'border-gray-300'
                      }`}
                    placeholder="email@exemple.com"
                    required
                  />
                  {formErrors.user.email && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.user.email}</p>
                  )}
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Téléphone
                  </label>
                  <input
                    type="tel"
                    name="user.tel"
                    value={formData.user.tel}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors ${formErrors.user.tel ? 'border-red-500 bg-red-50' : 'border-gray-300'
                      }`}
                    placeholder="+212 5XX XXX XXX"
                  />
                  {formErrors.user.tel && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.user.tel}</p>
                  )}
                </div>

                {/* Password Fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Mot de passe <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        name="user.password"
                        value={formData.user.password}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 pr-12 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors ${formErrors.user.password ? 'border-red-500 bg-red-50' : 'border-gray-300'
                          }`}
                        placeholder="Minimum 6 caractères"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => togglePasswordVisibility('password')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                    {formErrors.user.password && (
                      <p className="mt-1 text-sm text-red-600">{formErrors.user.password}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Confirmer le mot de passe <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        name="user.confirmPassword"
                        value={formData.user.confirmPassword}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 pr-12 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors ${formErrors.user.confirmPassword ? 'border-red-500 bg-red-50' : 'border-gray-300'
                          }`}
                        placeholder="Confirmez le mot de passe"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => togglePasswordVisibility('confirmPassword')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                    {formErrors.user.confirmPassword && (
                      <p className="mt-1 text-sm text-red-600">{formErrors.user.confirmPassword}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Security Info */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start">
                <Lock className="h-5 w-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                <div className="text-sm text-green-800">
                  <p className="font-medium mb-1">Sécurité :</p>
                  <p>
                    Le mot de passe sera automatiquement chiffré et sécurisé. Le propriétaire du laboratoire
                    pourra se connecter avec cet email et ce mot de passe pour gérer son laboratoire.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </ModalComponent>
  );
};

export default CombinedLabUserForm;